import { Link } from "react-router-dom";
import { Card, CardContent, Button, Badge } from "@learn-me/ui";
import { useWishlist, useRemoveFromWishlist } from "@/features/wishlist";
import { formatCurrency } from "@learn-me/utils";
import {
  Heart,
  BookOpen,
  Clock,
  Users,
  Trash2,
  ShoppingCart,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeIn } from "@/components/animated";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function WishlistPage() {
  const { data: wishlist, isLoading } = useWishlist();
  const removeMutation = useRemoveFromWishlist();

  const handleRemove = (courseId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    removeMutation.mutate(courseId);
  };

  return (
    <div className="space-y-8">
      <FadeIn direction="up">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Heart className="h-8 w-8 text-red-500" />
              Mis Favoritos
            </h1>
            <p className="text-muted-foreground mt-1">
              Cursos que has guardado para más tarde
            </p>
          </div>
          {wishlist && wishlist.length > 0 && (
            <Badge variant="outline" className="text-lg px-4 py-2">
              {wishlist.length} curso{wishlist.length !== 1 ? "s" : ""}
            </Badge>
          )}
        </div>
      </FadeIn>

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="aspect-video bg-muted" />
              <CardContent className="p-4">
                <div className="h-6 bg-muted rounded mb-2" />
                <div className="h-4 bg-muted rounded w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !wishlist?.length ? (
        <FadeIn>
          <Card>
            <CardContent className="py-16 text-center">
              <Heart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                Tu lista de favoritos está vacía
              </h2>
              <p className="text-muted-foreground mb-6">
                Explora nuestro catálogo y guarda los cursos que te interesen.
              </p>
              <Link to="/courses">
                <Button>
                  <BookOpen className="mr-2 h-5 w-5" />
                  Explorar cursos
                </Button>
              </Link>
            </CardContent>
          </Card>
        </FadeIn>
      ) : (
        <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {wishlist.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                <Link to={`/courses/${item.course.slug}`}>
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col group">
                    <div className="aspect-video relative bg-muted">
                      {item.course.thumbnailUrl ? (
                        <img
                          src={
                            normalizeImageUrl(item.course.thumbnailUrl) ?? ""
                          }
                          alt={item.course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}
                      {/* Remove button */}
                      <button
                        onClick={(e) => handleRemove(item.course.id, e)}
                        className="absolute top-2 right-2 p-2 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100"
                        title="Eliminar de favoritos"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>
                      {/* Free badge */}
                      {item.course.isFree && (
                        <Badge
                          variant="success"
                          className="absolute top-2 left-2"
                        >
                          Gratis
                        </Badge>
                      )}
                    </div>

                    <CardContent className="p-4 flex-1 flex flex-col">
                      <h3 className="font-semibold mb-2 line-clamp-2">
                        {item.course.title}
                      </h3>

                      {/* Course stats */}
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-3">
                        {item.course.chaptersCount > 0 && (
                          <span className="flex items-center gap-1">
                            <BookOpen className="h-3 w-3" />
                            {item.course.chaptersCount} lecciones
                          </span>
                        )}
                        {item.course.duration && item.course.duration > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {Math.round(item.course.duration / 60)}min
                          </span>
                        )}
                        {item.course.studentsCount > 0 && (
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {item.course.studentsCount}
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">
                        {item.course.shortDescription}
                      </p>

                      <div className="flex items-center justify-between mt-auto">
                        <span className="font-bold text-lg">
                          {item.course.isFree
                            ? "Gratis"
                            : formatCurrency(
                                item.course.salePrice ?? 0,
                                item.course.currency,
                              )}
                        </span>
                        <Button size="sm">
                          <ShoppingCart className="mr-2 h-4 w-4" />
                          {item.course.isFree ? "Inscribirse" : "Comprar"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
